/**
 * Marks core/video wrappers as started after first playback.
 *
 * @return {void}
 */
const initCoreVideoPosterOverlay = () => {

	const videosWithPoster = document.querySelectorAll( '.wp-block-video video[poster], .wp-block-video video[data-poster]' );

	if ( videosWithPoster.length === 0 ) {

		return;

	}

	videosWithPoster.forEach( ( video ) => {

		const wrapper = video.closest( '.wp-block-video' );

		if ( ! wrapper ) {

			return;

		}

		/**
		 * Marks wrapper after playback starts.
		 *
		 * @return {void}
		 */
		const markAsStarted = () => {

			wrapper.classList.add( 'is-video-started' );

		};

		video.addEventListener( 'play', markAsStarted );

		if ( ! video.paused && ! video.ended ) {

			markAsStarted();

		}

	} );

};

/**
 * Restores the poster image on core/video instances once the video nears the
 * viewport. The poster is server-side moved to `data-poster` (see
 * CoreMediaOptimization::addCoreVideoPreloadNone()) so it isn't fetched
 * eagerly on page load — mirrors the deferred poster pattern used by
 * `src/js/front/components/media-video.js`.
 *
 * @return {void}
 */
const initCoreVideoDeferredPoster = () => {

	const videosWithDeferredPoster = document.querySelectorAll( '.wp-block-video video[data-poster]' );

	if ( videosWithDeferredPoster.length === 0 ) {

		return;

	}

	/**
	 * Restore poster attribute from its deferred data attribute.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const hydratePoster = ( video ) => {

		const poster = video.getAttribute( 'data-poster' );

		if ( ! poster || video.getAttribute( 'poster' ) ) {

			return;

		}

		video.setAttribute( 'poster', poster );
		video.removeAttribute( 'data-poster' );

	};

	if ( 'IntersectionObserver' in window ) {

		const observer = new IntersectionObserver(
			( entries, currentObserver ) => {

				entries.forEach( ( entry ) => {

					if ( ! entry.isIntersecting ) {

						return;

					}

					hydratePoster( entry.target );
					currentObserver.unobserve( entry.target );

				} );

			},
			{
				rootMargin: '600px 0px',
			}
		);

		videosWithDeferredPoster.forEach( ( video ) => observer.observe( video ) );
		return;

	}

	videosWithDeferredPoster.forEach( ( video ) => hydratePoster( video ) );

};

/**
 * Promotes core/video preload from "none" to "auto" once video is near viewport.
 * Videos with a poster (own or still deferred as data-poster) are excluded and
 * stay deferred until user-initiated playback.
 *
 * @return {void}
 */
const initCoreVideoDeferredPreload = () => {

	const deferredVideos = document.querySelectorAll( '.wp-block-video video[preload="none"]:not([poster]):not([data-poster])' );

	if ( deferredVideos.length === 0 ) {

		return;

	}

	/**
	 * Set preload to auto so browser can fetch metadata/media.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const enablePreload = ( video ) => {

		video.setAttribute( 'preload', 'auto' );

	};

	if ( 'IntersectionObserver' in window ) {

		const observer = new IntersectionObserver(
			( entries, currentObserver ) => {

				entries.forEach( ( entry ) => {

					if ( ! entry.isIntersecting ) {

						return;

					}

					enablePreload( entry.target );
					currentObserver.unobserve( entry.target );

				} );

			},
			{
				rootMargin: '300px 0px',
			}
		);

		deferredVideos.forEach( ( video ) => observer.observe( video ) );
		return;

	}

	deferredVideos.forEach( ( video ) => enablePreload( video ) );

};

/**
 * Defers autoplay core/video playback until element is visible in viewport.
 *
 * @return {void}
 */
const initCoreVideoDeferredAutoplay = () => {

	const autoplayVideos = document.querySelectorAll( '.wp-block-video video[data-wombat-autoplay]' );

	if ( autoplayVideos.length === 0 ) {

		return;

	}

	/**
	 * Start playback safely and swallow policy-related promise rejections.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @return {void}
	 */
	const startPlayback = ( video ) => {

		video.setAttribute( 'preload', 'auto' );

		const playPromise = video.play();

		if ( playPromise && typeof playPromise.catch === 'function' ) {

			playPromise.catch( () => undefined );

		}

	};

	if ( 'IntersectionObserver' in window ) {

		const observer = new IntersectionObserver(
			( entries ) => {

				entries.forEach( ( entry ) => {

					if ( entry.isIntersecting ) {

						startPlayback( entry.target );
						return;

					}

					entry.target.pause();

				} );

			},
			{
				root: null,
				threshold: 0,
			}
		);

		autoplayVideos.forEach( ( video ) => observer.observe( video ) );
		return;

	}

	autoplayVideos.forEach( ( video ) => startPlayback( video ) );

};

/**
 * Adds click-to-toggle playback for non-autoplay core/video instances.
 *
 * @return {void}
 */
const initCoreVideoClickToggleWithoutControls = () => {

	const clickableVideos = document.querySelectorAll( '.wp-block-video video:not([data-wombat-autoplay])' );

	if ( clickableVideos.length === 0 ) {

		return;

	}

	clickableVideos.forEach( ( video ) => {

		video.addEventListener( 'click', ( event ) => {

			const hasControls = video.hasAttribute( 'controls' );

			if ( video.paused || video.ended ) {

				if ( ! hasControls ) {

					event.preventDefault();

				}

				const playPromise = video.play();

				if ( playPromise && typeof playPromise.catch === 'function' ) {

					playPromise.catch( () => undefined );

				}

				return;

			}

			if ( hasControls ) {

				return;

			}

			event.preventDefault();
			video.pause();

		} );

	} );

};

/**
 * Pauses core/video instances without controls after leaving the viewport.
 *
 * @return {void}
 */
const initCoreVideoPauseOutsideViewport = () => {

	const videosWithoutControls = document.querySelectorAll( '.wp-block-video video:not([controls]):not([data-wombat-autoplay])' );

	if ( videosWithoutControls.length === 0 || ! ( 'IntersectionObserver' in window ) ) {

		return;

	}

	const observer = new IntersectionObserver(
		( entries ) => {

			entries.forEach( ( entry ) => {

				if ( entry.isIntersecting ) {

					return;

				}

				entry.target.pause();

			} );

		},
		{
			root: null,
			threshold: 0,
		}
	);

	videosWithoutControls.forEach( ( video ) => observer.observe( video ) );

};

/**
 * Replaces lightweight YouTube placeholders with real iframes on click.
 *
 * @return {void}
 */
const initCoreYoutubeEmbedPlaceholders = () => {

	const placeholders = document.querySelectorAll( '.wombat-youtube-placeholder[data-youtube-video-id]' );

	if ( placeholders.length === 0 ) {

		return;

	}

	placeholders.forEach( ( placeholder ) => {

		placeholder.addEventListener( 'click', () => {

			const videoId = placeholder.getAttribute( 'data-youtube-video-id' );
			const iframeTitle = placeholder.getAttribute( 'data-youtube-title' ) || '';

			if ( ! videoId ) {

				return;

			}

			const iframe = document.createElement( 'iframe' );
			iframe.src = `https://www.youtube.com/embed/${ videoId }?autoplay=1&rel=0`;
			iframe.title = iframeTitle;
			iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
			iframe.allowFullscreen = true;
			iframe.loading = 'lazy';

			placeholder.replaceWith( iframe );

		} );

	} );

};

/**
 * Initializes all core media optimizations on the page.
 *
 * @return {void}
 */
const initCoreMediaOptimizations = () => {

	initCoreVideoPosterOverlay();
	initCoreVideoDeferredPoster();
	initCoreVideoDeferredPreload();
	initCoreVideoDeferredAutoplay();
	initCoreVideoClickToggleWithoutControls();
	initCoreVideoPauseOutsideViewport();
	initCoreYoutubeEmbedPlaceholders();

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', initCoreMediaOptimizations );

} else {

	initCoreMediaOptimizations();

}
